import { transform } from 'lodash-es'
import WbTextBox from '../textBox'
import WbFrame from '../frame'
import WbArticleImage from '../articleImage'
import WbArticleDetails from '../articleDetails'
import type { IWhiteboardTemplate, IWhiteboardTemplateFileMapping, IWhiteboardTemplateOption } from './IWhiteboardTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { whiteboardConstants } from '@/models/constants'
import utils from '@/services/utils'
import type MyArticle from '@/models/myArticle'
import appConfig from '@/services/appConfig'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import levisMensBottomsRTFinishImage from '@/assets/templateLayoutImages/levisMensBottomsRTFinish.png'
import type LinkedCustomer from '@/models/linkedCustomer'

class LevisMensBottomsRTFinish implements IWhiteboardTemplate {
  id = whiteboardConstants.frameTemplatesId.LevisMensBottomsRTFinish
  name = 'Levi\'s Men\'s Bottoms RT Finish - Print'
  layoutImage = levisMensBottomsRTFinishImage
  imgSize: number = 298

  settings = {
    articleImage: { width: 500, height: 500, scale: (this.imgSize * 100) / 500 },
    articleDetails: { width: 298, fontFamily: 'Roboto', fontSize: 20, fontWeight: 'normal', fill: '#000000', textAlign: 'left', attributes: [] as string[], showLabels: false },
    textBox: { fontFamily: 'Roboto', fontSize: 48, fontStyle: 'normal' as 'normal' | 'italic', fontWeight: 'bold', fill: '#000000', textAlign: 'left' },
  }

  imgHorSpacing: number = 20
  imgVerSpacing: number = 20
  titleTopPadding: number = 50
  titleLeftPadding: number = 30
  frameWidth: number = 1632
  frameHeight: number = 1056
  headerHeight: number = 200

  leftMargin: number = 42
  spacingBetweenFrames = 50

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>): IWhiteboardTemplateOption[] {
    const attributes: IMyAttribute[] = []
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }

    // TODO: Implement configurable options
    const options: IWhiteboardTemplateOption[] = []
    let defaultForCarryoverArticleWithNoImageAttributesList: string [] = ['ColorwayCodePc9_Levi']
    defaultForCarryoverArticleWithNoImageAttributesList = defaultForCarryoverArticleWithNoImageAttributesList.filter(attribute => myAttributes[attribute])
    options.push({
      name: 'attributesOnImageForCarryoverArticle',
      label: 'generateFrameDialog.steps.options.attributesOnImageForCarryoverArticle',
      type: 'list',
      default: defaultForCarryoverArticleWithNoImageAttributesList,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    let defaultAttributesList: string [] = ['MerchPlaceholderCode_Levi', 'ColorwayCodePc9_Levi', 'ProductLifecycleGroup_Levi', 'ShadeOrder_Levi', 'ColorwayDescription_Levi', 'FinishDescription_Levi', 'InitialUnitCostWithCurrency_Levi', 'BLPCustomAttribute1_Levi']
    defaultAttributesList = defaultAttributesList.filter(attribute => myAttributes[attribute])

    options.push({
      name: 'attributesOnImageForNewArticle',
      label: 'generateFrameDialog.steps.options.attributesOnImageForNewArticle',
      type: 'list',
      default: defaultAttributesList,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    let defaultDisplayAttributesListForNew = ['SharedFinish_Levi', 'ReplacementFinish_Levi', 'BlpNotes_Levi', 'MaterialCodeFaCode_Levi', 'BLPCustomAttribute2_Levi', 'TargetCluster_Levi']
    defaultDisplayAttributesListForNew = defaultDisplayAttributesListForNew.filter(attribute => myAttributes[attribute])

    options.push({
      name: 'attributesToDisplayForNewArticles',
      label: 'generateFrameDialog.steps.options.attributesToDisplayForNewArticles',
      type: 'attributeChooser',
      default: defaultDisplayAttributesListForNew,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    let defaultDisplayAttributesListForCarryover = ['ColorwayName_Levi', 'ColorLookCodePlmLoaderFile_Levi', 'PrimaryFabric_Levi', 'TargetCluster_Levi', 'UnitsBlp_Levi', 'SeasonFirstIntroduced_Levi', 'LastSeasonOffered_Levi', 'UnitCostWithCurrency_Levi']
    defaultDisplayAttributesListForCarryover = defaultDisplayAttributesListForCarryover.filter(attribute => myAttributes[attribute])
    options.push({
      name: 'attributesToDisplayForCarryoverArticles',
      label: 'generateFrameDialog.steps.options.attributesToDisplayForCarryoverArticles',
      type: 'attributeChooser',
      default: defaultDisplayAttributesListForCarryover,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    // options.push({
    //   name: 'numberOfArticlesOnEachFrame',
    //   label: 'generateDialog.steps.options.numberOfArticlesOnEachFrame',
    //   type: 'number',
    //   default: 4,
    //   min: 2,
    //   max: 10,
    //   required: false,
    // })
    return options
  }

  getFileMapping(): IWhiteboardTemplateFileMapping[] {
    const mapping: IWhiteboardTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    })
    return mapping
  }

  // TODO: Need to work on loading custom fonts
  async generate(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articlesData: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, startPosition: IPoint, direction: 'horizontal' | 'vertical', excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    this.spacingBetweenFrames = (direction === 'vertical') ? 100 : 50
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, startPosition, direction, isExporting, retailPg, wholesalePg, outletPg)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, startPosition: IPoint, direction: 'horizontal' | 'vertical', isExporting: boolean, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup): Promise<IWbObject[]> {
    const objects: IWbObject[] = []
    const frameCount = 0
    const priceGroups: Record<string, CatalogPriceGroup | undefined> = {
      wholesale: wholesalePg,
      retail: retailPg,
      outlet: outletPg,
    }
    const frameLeft = startPosition.x ? startPosition.x : 0
    const frameTop = startPosition.y ? startPosition.y : 0
    // this.calculateImageProps(options.numberOfArticlesOnEachFrame || 4)
    const groups = await this.generateGroups(catalog, articles, options, myAttributes, priceGroups)
    for (const [, value] of Object.entries(groups)) {
      objects.push(...await this.generateFrameForEachKey(catalog, value, myAttributes, options, frameLeft, frameTop, frameCount, isExporting, direction))
    }
    return objects
  }

  // calculateImageProps(numberOfArticlesOnEachFrame) {
  //   const spaceAvailableForEachImage = (this.frameWidth - this.leftMargin) / numberOfArticlesOnEachFrame

  //   this.imgHorSpacing = (spaceAvailableForEachImage * 0.10)
  //   this.imgSize = spaceAvailableForEachImage - this.imgHorSpacing
  //   if (this.imgSize > 500) {
  //     this.imgSize = 500
  //     this.imgHorSpacing = spaceAvailableForEachImage - this.imgSize
  //   }
  // }

  async generateFrameForEachKey(catalog, groupDetails: Record<string, any>, myAttributes: Record<string, IMyAttribute>, options: Record<string, string[]>, frameLeft: number, frameTop: number, frameCount: number, isExporting: boolean, direction: 'horizontal' | 'vertical') {
    let frameObjects: IWbObject[] = []
    const objects: IWbObject [] = []
    let top = frameTop
    const numberOfArticlesOnEachFrame = 5
    frameObjects.push(this.createTitleObj(groupDetails.headerText, frameLeft, top))
    top += this.headerHeight
    const articles = groupDetails.articles
    const sortedStateName = ['New', 'Carryover']
    articles.sort((a, b) => sortedStateName.indexOf(a.StateName) - sortedStateName.indexOf(b.StateName))
    for (let index = 0; index < articles.length; index++) {
      const article = articles[index]
      if (index !== 0 && (index % numberOfArticlesOnEachFrame === 0)) {
        // new page
        objects.push(...frameObjects)
        objects.push(new WbFrame('ledger', { children: frameObjects.map(obj => obj.id || ''), name: `${groupDetails.headerText + (frameCount === 0 ? '' : `(${frameCount})`)}`, left: isExporting ? 0 : frameLeft, top: isExporting ? 0 : frameTop, sortOrder: frameCount }))
        frameObjects = []
        if (direction === 'vertical') {
          frameTop += isExporting ? 0 : this.frameHeight + this.spacingBetweenFrames
        }
        else {
          frameLeft += isExporting ? 0 : this.frameWidth + this.spacingBetweenFrames
        }
        top = frameTop
        frameObjects.push(this.createTitleObj(groupDetails.headerText, frameLeft, top))
        top += this.headerHeight
      }
      // draw article Images and there details
      const currentImageInRow = (index) % numberOfArticlesOnEachFrame
      const imageLeft = this.leftMargin + frameLeft + currentImageInRow * (this.imgSize + this.imgHorSpacing)
      this.settings.articleDetails.attributes = article.StateName?.toLowerCase() === 'carryover' ? options.attributesOnImageForCarryoverArticle : options.attributesOnImageForNewArticle
      const articleImageObj = await this.createImage(catalog, top, imageLeft, article, this.settings.articleDetails.attributes, isExporting)
      frameObjects.push(articleImageObj)
      const detailsTop = top + this.imgSize + this.imgVerSpacing
      // if (attributesToDisplay.length !== 0) {
      const articleDetailsObject = await this.getArticleDetailsObject(article, this.settings.articleDetails.attributes, myAttributes, detailsTop, imageLeft)
      if (isExporting) {
        const obj = articleDetailsObject.toObject() as WbArticleDetails
        obj.text = articleDetailsObject.text
        frameObjects.push(obj)
      }
      else {
        frameObjects.push(articleDetailsObject)
      }
      // }
    }
    objects.push(...frameObjects)
    objects.push(new WbFrame('ledger', { children: frameObjects.map(obj => obj.id || ''), name: `${groupDetails.headerText + (frameCount === 0 ? '' : `(${frameCount})`)}`, left: isExporting ? 0 : frameLeft, top: isExporting ? 0 : frameTop, sortOrder: frameCount }))
    if (direction === 'vertical') {
      frameTop += isExporting ? 0 : this.frameHeight + this.spacingBetweenFrames
    }
    else {
      frameLeft += isExporting ? 0 : this.frameWidth + this.spacingBetweenFrames
    }
    return objects
  }

  async generateGroups(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, priceGroups?: Record<string, CatalogPriceGroup | undefined>) {
    const groups: Record<string, any> = {}
    let attributesForGrouping: string[] = ['ProductPricePositioning_Levi', 'DittoCollection_Levi', 'ProductName_Levi', 'ProductStory_Levi', 'BLPCustomAttribute2_Levi', 'ArticleNumber']
    attributesForGrouping = attributesForGrouping.reduce((acu, attribute) => {
      if (utils.isDefined(myAttributes[attribute])) {
        acu.push(attribute)
      }
      return acu
    }, [] as Array<string>)
    utils.sort(articles, attributesForGrouping)
    for (let i = 0; i < articles.length; i++) {
      const article = articles[i]
      let headerText = ''
      headerText += (await utils.getAttributeTypeSpecificValue(myAttributes.ProductPricePositioning_Levi, article, appConfig.DB, catalog, priceGroups))
      headerText += ' '
      headerText += (await utils.getAttributeTypeSpecificValue(myAttributes.DittoCollection_Levi, article, appConfig.DB, catalog, priceGroups))
      headerText += ': '
      headerText += (await utils.getAttributeTypeSpecificValue(myAttributes.ProductName_Levi, article, appConfig.DB, catalog, priceGroups))
      headerText += ' - '
      headerText += (await utils.getAttributeTypeSpecificValue(myAttributes.ProductStory_Levi, article, appConfig.DB, catalog, priceGroups))
      headerText += ' - '
      const tmp = (await utils.getAttributeTypeSpecificValue(myAttributes.BLPCustomAttribute2_Levi, article, appConfig.DB, catalog, priceGroups))
      headerText += tmp.toString().length > 0 ? (await utils.getAttributeTypeSpecificValue(myAttributes.BLPCustomAttribute2_Levi, article, appConfig.DB, catalog, priceGroups)) : '[Blank]'
      const key = headerText.toLowerCase()
      if (groups[key]) {
        groups[key].articles.push(article)
      }
      else {
        groups[key] = {
          headerText,
          articles: [article],
        }
      }
    }
    return groups
  }

  async getArticleDetailsObject(article: MyArticle, articleAttributes: string[], myAttributes: Record<string, IMyAttribute>, top: number, left: number, isExporting: boolean = false) {
    const opt = {
      left,
      top,
      attributes: articleAttributes,
      width: this.settings.articleDetails.width,
      showLabels: this.settings.articleDetails.showLabels,
      fontFamily: this.settings.articleDetails.fontFamily,
      fontSize: this.settings.articleDetails.fontSize,
      fontWeight: this.settings.articleDetails.fontWeight,
      textAlign: this.settings.articleDetails.textAlign,
      fill: this.settings.articleDetails.fill,
    }
    const object = await WbArticleDetails.loadArticleDetails(article, opt, !isExporting)
    return object
  }

  // objects functions
  createTitleObj(title: string, left: number, top: number) {
    return new WbTextBox(title, {
      top: this.titleTopPadding + top,
      left: this.titleLeftPadding + left,
      width: this.frameWidth - 20,
      fontFamily: this.settings.textBox.fontFamily,
      fontSize: this.settings.textBox.fontSize,
      fontStyle: this.settings.textBox.fontStyle,
      fontWeight: this.settings.textBox.fontWeight,
      fill: this.settings.textBox.fill,
      textAlign: this.settings.textBox.textAlign,
      lock: false,
    })
  }

  async createImage(catalog: CatalogDetails, top: number, left: number, article: MyArticle, attributesOnImage: string[], isExporting: boolean) {
    let configuredColorBackgroundColor = catalog.Config?.NoImageColorCodeForLevisTemplate
    configuredColorBackgroundColor = transform(configuredColorBackgroundColor, (result, val, key) => {
      result[key.toLowerCase()] = val
    })
    const productLifeCycleGroupValue: string = article.ProductLifecycleGroup_Levi?.toString() || 'blank'
    const noImageObjectProperties: Record<string, any> = {}
    noImageObjectProperties.backgroundColor = article.StateName && article.StateName.toLowerCase() === 'carryover' ? '#D8D8D8' : configuredColorBackgroundColor[productLifeCycleGroupValue.toLowerCase()] || '#ffffff'
    noImageObjectProperties.attributes = attributesOnImage
    noImageObjectProperties.border = { borderColor: utils.isDefined(article.SharedFinish_Levi) ? '#9ad054' : '#000000', strokeWidth: utils.isDefined(article.SharedFinish_Levi) ? isExporting ? 6 : 24 : 1 }
    const opt = {
      top,
      left,
      scaleX: this.settings.articleImage.scale / 100,
      scaleY: this.settings.articleImage.scale / 100,
      catalogCode: article.CatalogCode,
      articleId: article.Id,
      objectId: article.CatalogArticleId,
      isRequest: article._IsRequestArticle,
      noImageObjectProperties,
    }
    if (isExporting) {
      return await WbArticleImage.getJsonObject(article, this.settings.articleImage.width, this.settings.articleImage.height, opt)
    }
    else {
      return await WbArticleImage.loadArticleImage(article, this.settings.articleImage.width, this.settings.articleImage.height, opt)
    }
  }
}

export default new LevisMensBottomsRTFinish()
